import { z } from 'zod';

/** Every role a person can hold. Everyone is an Employee; the rest add to it (R1: roles blend). */
export const RoleKey = z.enum([
  'employee',
  'manager',
  'sector_lead',
  'delegate',
  'executive_delegate',
  'requester',
  'executive',
  'stock_admin',
  'payroll',
  'finance',
  'system_admin',
  'paa_taa_approver',
  'spot_reviewer',
  'support',
]);
export type RoleKey = z.infer<typeof RoleKey>;

const Ref = z.object({ id: z.string(), name: z.string() });

export const RoleAssignment = z.object({
  role: RoleKey,
  /** Sector Leads and delegates act for one sector. */
  sector: Ref.nullable(),
  /** Delegates: the Sector Lead or Executive they enter awards for. */
  forPerson: Ref.nullable(),
  /** Delegates: whether their entries go to that person first. */
  needsApproval: z.boolean().nullable(),
  /** Delegates: the award types they may enter for that person. */
  awardTypes: z.array(z.enum(['EDB', 'SDB', 'PAA', 'TAA'])).nullable(),
});
export type RoleAssignment = z.infer<typeof RoleAssignment>;

/**
 * Level 1 navigation, in its fixed order (CLAUDE.md "Roles blend": Home · My rewards · Recognize ·
 * Manage · Insights · Settings). APP-236: the Executive pages are Manage's Executive group, so
 * Executive is no Level 1 item. Items a person can't use are absent.
 */
export const LevelOne = z.enum([
  'home',
  'my-rewards',
  'recognize',
  'manage',
  'insights',
  'admin',
  'settings',
]);
export type LevelOne = z.infer<typeof LevelOne>;

/**
 * APP-234: one page under Manage, the work a person does for others. Each is a Level 2 tab; the
 * web knows each one's words and address. A page some groups share (All Spot awards) is one id.
 */
export const ManagePageId = z.enum([
  // The Spot Committee's.
  'spot-review',
  'spot-all',
  'spot-letters',
  'spot-statements',
  // A nominee's supervisor's.
  'spot-decisions',
  // The Stock Admin team's.
  'queue',
  'letters',
  'earning-statements',
  'shareworks',
  // Payroll's.
  'payroll',
  // The Executives' (APP-236: Budget pools and Values & requesters moved in from Executive).
  'budget-pools',
  'values',
  'spot-funding',
  // An Executive's, a Sector Lead's or a PAA/TAA approver's.
  'approvals',
  // APP-245: a letter holder's, under Sector Lead for a lead or delegate, else My team.
  'letters-to-hand-out',
]);
export type ManagePageId = z.infer<typeof ManagePageId>;

/**
 * The role a group of Manage's pages is for: APP-236's pill in Manage's first row, named by the
 * role whose work its pages are (APP-234 D2c).
 */
export const ManageGroupId = z.enum([
  'executive',
  'stock_admin',
  'payroll',
  'sector_lead',
  'approver',
  'committee',
  'team',
]);
export type ManageGroupId = z.infer<typeof ManageGroupId>;

/** One group of Manage's pages: the role it's for and its pages, in order. Never empty. */
export const ManageGroup = z.object({
  id: ManageGroupId,
  pages: z.array(ManagePageId).min(1),
});
export type ManageGroup = z.infer<typeof ManageGroup>;

/**
 * APP-236 §4: a person's main work in Manage. Manage shows `id`'s group first and opens on it:
 * the person's own choice (My preferences › Main work) while they have that group, else
 * `automatic`, worked out from the roles they hold. `choices` are their groups in the standard
 * order, what Main work offers; it shows with two or more.
 */
export const ManageMain = z.object({
  id: ManageGroupId,
  automatic: ManageGroupId,
  choices: z.array(ManageGroupId).min(1),
});
export type ManageMain = z.infer<typeof ManageMain>;

/** Self-entered pronouns (SET1, APP-081): optional, never on letters, emails or audit labels. */
export const Pronouns = z.enum(['she/her', 'he/him', 'they/them']);
export type Pronouns = z.infer<typeof Pronouns>;

/**
 * Where a person lands after sign-in (SET1, APP-081): one of their own Level 1 items. Settings
 * isn't one; a link someone followed (an email's) still opens its own page. APP-236: Manage is
 * one, opening on the person's main work; a start page saved as Executive, before the Executive
 * pages moved into Manage, reads as Manage (`startPageFor`).
 */
export const StartPage = z.enum(['home', 'my-rewards', 'recognize', 'manage', 'insights', 'admin']);
export type StartPage = z.infer<typeof StartPage>;

export const HeroPreference = z.enum(['full', 'slim']);
/** `system` follows the device's reduced-motion setting; `reduced` always turns motion off. */
export const MotionPreference = z.enum(['system', 'reduced']);

export const Me = z.object({
  person: z.object({
    id: z.string(),
    displayName: z.string(),
    /** The name we greet you by (My settings); falls back to the first name. */
    greetingName: z.string(),
    initials: z.string(),
    jobTitle: z.string().nullable(),
    sector: Ref.nullable(),
    eeNumber: z.string().nullable(),
    /** Shown next to the name on the avatar menu's profile card only. Optional for older readers. */
    pronouns: Pronouns.nullable().optional(),
  }),
  roles: z.array(RoleAssignment),
  /** The Level 1 items this person sees, decided on the server from their roles. */
  nav: z.array(LevelOne),
  canPreview: z.boolean(),
  /**
   * Preview as (TEST1, APP-059): the role combination being previewed, or null. While it's set,
   * `roles` and `nav` describe that combination, not the person; the server still authorizes every
   * call with the person's own roles, and nothing can be changed (APP-030).
   */
  preview: z
    .object({
      roles: z.array(RoleKey),
      /** The sector a previewed Sector Lead or Delegate works in, or null. */
      sector: Ref.nullable(),
    })
    .nullable(),
  /**
   * APP-122 (dev and test only): this session acts as a real person. `person`, `roles`, `nav` and
   * everything else are theirs, with live data, and the server authorizes every call as them;
   * `signedInAs` is who is really signed in, for the banner. `preview` is null meanwhile.
   * Optional for older readers.
   */
  actingAs: z.object({ signedInAs: Ref, startedAt: z.string() }).nullable().optional(),
  /**
   * APP-232: this person reviews Spot nominations today: the committee named in Spot Award setup,
   * or until one is named the Executives and anyone granted the seat. The same answer as Preview
   * as' Spot Committee card (APP-219). The role line, Your roles and the Preview banner name the
   * seat from it; `roles` stays the person's grants. Absent in a sample preview, where `roles` is
   * the previewed combination, and from older servers.
   */
  spotCommittee: z.boolean().optional(),
  /**
   * APP-234: Manage's pages for this person, grouped by role, from `manageMenuFor`, the rule `nav`
   * comes from too, so Level 1 shows Manage exactly when this has a group. APP-236: in display
   * order, main work first (`manageLayoutFor`). Navigation only: every page still checks access
   * itself. Optional for older servers; the web then works it out from `roles` alone, as a sample
   * preview does.
   */
  manage: z.array(ManageGroup).optional(),
  /**
   * APP-236 §4: which of `manage`'s groups is the person's main work, and what Automatic means for
   * them. `manage` is already in display order, main work first: the web never reorders it. Absent
   * without Manage, and from older servers.
   */
  manageMain: ManageMain.optional(),
  preferences: z.object({
    hero: HeroPreference,
    motion: MotionPreference,
    /**
     * Where sign-in lands, already checked against `nav` (Home when the saved page isn't one of
     * theirs any more). Optional, so older readers still fit.
     */
    startPage: StartPage.optional(),
  }),
  /** What the app offers this person right now (APP-104). Optional, so older readers still fit. */
  features: z
    .object({
      feedback: z.object({
        enabled: z.boolean(),
        /**
         * Voice is offered to this person now (APP-104 §7), even when today's voice time is used
         * up, so the button can say so instead of disappearing.
         */
        voice: z.boolean(),
        /** Voice time left today, in seconds (0 when used up). Optional for older readers. */
        voiceSecondsLeft: z.number().int().nonnegative().optional(),
        /** Show the dictation tip (a setting; the dialog also checks voice and the platform). Optional for older readers. */
        dictationTip: z.boolean().optional(),
        /**
         * APP-138: whether this person has sent any feedback, and the team's replies they haven't
         * read, for Settings › My feedback and its "N new" badge. Optional for older readers.
         */
        mine: z.object({ any: z.boolean(), newReplies: z.number().int().min(0) }).optional(),
      }),
      /**
       * APP-148 D1: whether the Stock Admin team may act for Executives and for Sector Leads now,
       * for the people the switches' own view list allows (Stock Admins, Executives, System
       * Admins); left out for everyone else. Optional for older readers.
       */
      actingFor: z.object({ executives: z.boolean(), sectorLeads: z.boolean() }).optional(),
    })
    .optional(),
  /** The organization time zone (APP-018): every time is shown in it, apart from the audit trail's UTC. */
  organization: z.object({
    timeZone: z.string(),
    /** The footer's "Awards policy" link (Admin, https only); null leaves the link out. */
    awardsPolicyUrl: z.url().nullable(),
    /**
     * The fiscal year (APP-018): the day it starts and the year today is in, named by the year it
     * starts in (a July 1 start makes Jul 1, 2026 to Jun 30, 2027 fiscal 2026). Optional, so older
     * readers still fit; left out, a year starts on January 1.
     */
    fiscalYear: z
      .object({
        start: z.object({
          month: z.number().int().min(1).max(12),
          day: z.number().int().min(1).max(28),
        }),
        current: z.number().int(),
      })
      .optional(),
  }),
});
export type Me = z.infer<typeof Me>;

export const SignOutResponse = z.object({ redirectTo: z.string() });
export type SignOutResponse = z.infer<typeof SignOutResponse>;

/** What the sign-in page needs before anyone is signed in: which splash to show (SYS5). */
export const SignInOptions = z.object({ splash: z.enum(['video', 'photo_wall']) });
export type SignInOptions = z.infer<typeof SignInOptions>;
